import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useComdispUi } from '@/hooks/useComdispUi';
import {
  AlertCircle, AlertTriangle, BatteryCharging, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight,
  Clock, Code2, Coins, Download, FileCode2, Info, Save, Scale, Send, ShieldAlert, Sparkles, Sun, TrendingDown,
  type LucideIcon,
} from 'lucide-react';

const textMuted = '#6B6B70';
const accent = '#4F9DFF';
const green = '#39D98A';
const red = '#FF6B6B';
const amber = '#F5A623';

interface Asset { id: string; assetName: string; eicCode: string; installedCapacityMw: string | number; region?: string; operationalStatus: string; }
type Mode = 'charge' | 'discharge' | 'idle';

const num = (n: any, d = 0, suf = '') =>
  n == null || isNaN(Number(n)) ? '—'
    : `${Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d })}${suf ? ' ' + suf : ''}`;
const short = (s: string) => (s || '').replace(/^СЕС\s+/, '').trim();
const tomorrow = () => new Date(Date.now() + 864e5).toLocaleDateString('en-CA');
const fmtKyiv = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const longDate = (ymd: string) => {
  const d = new Date(ymd + 'T00:00:00');
  return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
};
const shortDate = (ymd: string) => ymd.split('-').reverse().join('.');
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  const s = new Date(y, mo - 1, 1).toLocaleDateString('uk-UA', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Кольори серій для recharts — SVG-атрибути не резолвлять var(--…), тож
 * токени дістаємо через getComputedStyle. Перерахунок прив'язаний до isDark.
 * Догма: сонце → --ok, розряд УЗЕ → --ok-2, заряд → --warn, підсумок → --series-3.
 */
function useCdColors(isDark: boolean) {
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      sun: v('--ok', isDark ? '#4CAF6A' : '#2E7D46'),
      dis: v('--ok-2', isDark ? '#40C094' : '#1F8A6B'),
      chg: v('--warn', isDark ? '#E0A24A' : '#C77A1B'),
      netLine: v('--series-3', isDark ? '#C56467' : '#A6575B'),
      grid: v('--line', isDark ? 'rgba(255,255,255,0.08)' : '#E4E5E7'),
      dim: v('--text-dim', isDark ? '#7E868E' : '#9AA0A6'),
      text: v('--text', isDark ? '#F2F2F2' : '#28323C'),
      surface: v('--surface-solid', isDark ? '#2F3A45' : '#FFFFFF'),
    };
  }, [isDark]);
}

// Card is defined OUTSIDE the page component so it is a STABLE component type.
// (Previously it was declared inline, which remounted the whole subtree on every
//  render — that stole focus from inputs after one keystroke and scrolled to top.)
function Card({ title, subtitle, children, right, tk }: any) {
  return (
    <div style={{ background: tk.cardBg, border: `1px solid ${tk.cardBorder}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: tk.textMain }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ComDispPage() {
  const { isDark } = useTheme();
  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';
  const rowBorder = isDark ? 'rgba(255,255,255,0.04)' : '#EEF0F2';
  const codeColor = isDark ? '#9AD' : '#2563EB';
  const tk = { cardBg, cardBorder, textMain };

  const { selectedAsset, setSelectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id || '';
  const [assets, setAssets] = useState<Asset[]>([]);
  // Дуальний режим (§6 handoff): стенд бачить редизайн, прод — незмінений
  // legacy-рендер нижче. Доба поставки при редизайні живе в топбарі.
  const isRd = import.meta.env.VITE_REDESIGN === '1';
  const cc = useCdColors(isDark);
  const cdUi = useComdispUi();
  const [localDate, setLocalDate] = useState(tomorrow());
  const date = isRd ? cdUi.date : localDate;
  const setDate = (d: string) => { if (isRd) cdUi.setDate(d); else setLocalDate(d); };
  const [month, setMonth] = useState(new Date().toLocaleDateString('en-CA').slice(0, 7));

  const [net, setNet] = useState<any>(null);
  const [mms, setMms] = useState<any>(null);
  const [rev, setRev] = useState(1);
  const [bess, setBess] = useState<Record<number, { mode: Mode; powerKw: number }>>({});
  const [bessCfg, setBessCfg] = useState<any>(null);
  const [bessDiag, setBessDiag] = useState<any>(null);
  const [agentStatus, setAgentStatus] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [imbPriced, setImbPriced] = useState<any>(null);
  const [lost, setLost] = useState<any>(null);
  const [xmlOpen, setXmlOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState<{ kind: 'ok' | 'warn' | 'err'; msg: string } | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [dayHours, setDayHours] = useState<Record<string, any[]>>({});

  const api = useCallback(async (path: string, init?: RequestInit) => {
    const r = await authFetch(`${API_BASE_URL}${path}`, init);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j.message || j.error || r.statusText), { data: j, status: r.status });
    return j;
  }, []);

  const flash = (kind: 'ok' | 'warn' | 'err', msg: string) => { setToast({ kind, msg }); setTimeout(() => setToast(null), 6000); };

  const pickAsset = (a: Asset) => setSelectedAsset({
    id: a.id, assetName: a.assetName, installedCapacityMw: Number(a.installedCapacityMw) || 0,
    region: a.region || '', operationalStatus: a.operationalStatus || 'active',
  });

  // load assets once; keep the globally selected station (persists across menu items)
  useEffect(() => {
    authFetch(`${API_BASE_URL}/admin/assets`).then((r) => r.json()).then((a: Asset[]) => {
      const live = (a || []).filter((x) => x.eicCode);
      setAssets(live);
      if (live.length && !selectedAsset) pickAsset(live[0]);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const loadForDate = useCallback(async () => {
    if (!assetId) return;
    try {
      const [n, m, b, ag] = await Promise.all([
        api(`/comdisp/net-forecast?assetId=${assetId}&date=${date}`),
        api(`/comdisp/mms/schedule?assetId=${assetId}&date=${date}`).catch(() => null),
        api(`/comdisp/bess-schedule?assetId=${assetId}&date=${date}`),
        api(`/comdisp/bess-agent/status?assetId=${assetId}`).catch(() => null),
      ]);
      setNet(n); setMms(m); setBessCfg(b.config || null); setAgentStatus(ag);
      const bm: Record<number, { mode: Mode; powerKw: number }> = {};
      (b.hours || []).forEach((h: any) => { bm[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(bm);
    } catch (e: any) { flash('err', e.message); }
  }, [assetId, date, api]);

  const loadMonthly = useCallback(async () => {
    if (!assetId) return;
    const to = new Date().toLocaleDateString('en-CA');
    const from = new Date(Date.now() - 29 * 864e5).toLocaleDateString('en-CA');
    try {
      const [bal, ip, lg] = await Promise.all([
        api(`/comdisp/balance?assetId=${assetId}&month=${month}-01`),
        api(`/comdisp/imbalance-priced?assetId=${assetId}&from=${from}&to=${to}`),
        api(`/comdisp/lost-generation?assetId=${assetId}&from=${from}&to=${to}`),
      ]);
      setBalance(bal); setImbPriced(ip); setLost(lg); setOpenDay(null); setDayHours({});
    } catch (e: any) { flash('err', e.message); }
  }, [assetId, month, api]);

  useEffect(() => { loadForDate(); }, [loadForDate]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const toggleDay = async (day: string) => {
    if (openDay === day) { setOpenDay(null); return; }
    setOpenDay(day);
    if (!dayHours[day]) {
      try {
        const r = await api(`/comdisp/balance-hours?assetId=${assetId}&day=${day}`);
        setDayHours((p) => ({ ...p, [day]: r.hours || [] }));
      } catch (e: any) { flash('err', e.message); }
    }
  };

  const chartData = useMemo(() => (net?.hours || []).map((h: any, i: number) => ({
    hour: String(i + 1).padStart(2, '0'),
    sun: Number(h.pvKwh) / 1000,
    net: Number(h.netKwh) / 1000,
    charge: -Number(h.chargeKwh) / 1000,
    discharge: Number(h.dischargeKwh) / 1000,
  })), [net]);

  // Fool-proof УЗЕ: перевірка потужності на годину + баланс SoC (старт доби = 0, порожня).
  const bessCheck = useMemo(() => {
    const maxP = bessCfg?.maxPowerKw ?? Infinity;
    const cap = bessCfg?.usableKwh ?? Infinity;
    const errByHour: Record<number, string> = {};
    const msgs: string[] = [];
    let soc = 0;
    for (let h = 1; h <= 24; h++) {
      const cell = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
      const p = Math.max(0, Number(cell.powerKw) || 0);
      const hh = String(h).padStart(2, '0');
      if (p > maxP + 1e-6) { errByHour[h] = `Потужність ${p} > ${maxP} кВт`; msgs.push(`Год ${hh}: потужність ${p} кВт перевищує ліміт ${maxP} кВт`); }
      if (cell.mode === 'charge') {
        soc += p;
        if (soc > cap + 1e-6) { errByHour[h] = errByHour[h] || `Перевищено ємність (${cap} кВт·год)`; msgs.push(`Год ${hh}: заряд перевищує корисну ємність УЗЕ (${cap} кВт·год)`); soc = cap; }
      } else if (cell.mode === 'discharge') {
        soc -= p;
        if (soc < -1e-6) { errByHour[h] = errByHour[h] || 'Розряд перевищує накопичене'; msgs.push(`Год ${hh}: розряд перевищує накопичений заряд УЗЕ`); soc = 0; }
      }
    }
    return { errByHour, msgs };
  }, [bess, bessCfg]);

  const setBessHour = (hour: number, patch: Partial<{ mode: Mode; powerKw: number }>) =>
    setBess((p) => ({ ...p, [hour]: { mode: 'idle', powerKw: 0, ...p[hour], ...patch } }));

  const saveBess = async () => {
    setBusy('bess');
    try {
      const hours = Object.entries(bess).map(([h, v]) => ({ hour: Number(h), mode: v.mode, powerKw: v.powerKw }));
      await api('/comdisp/bess-schedule', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, setBy: 'owner-ui', hours }),
      });
      flash('ok', 'Графік УЗЕ збережено'); await loadForDate();
    } catch (e: any) { flash('err', e.message); } finally { setBusy(''); }
  };

  const autofillBess = async () => {
    setBusy('autofill');
    try {
      const r = await api('/comdisp/bess-autofill', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date }),
      });
      const bm: Record<number, { mode: Mode; powerKw: number }> = {};
      (r.hours || []).forEach((h: any) => { bm[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(bm); setBessDiag(r);
      flash(r.socSource === 'live' ? 'ok' : 'warn', r.note);
    } catch (e: any) { flash(e.status === 404 ? 'warn' : 'err', e.message); } finally { setBusy(''); }
  };

  const genXml = async () => {
    setBusy('gen');
    try {
      const r = await api('/comdisp/mms/generate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, revision: rev }),
      });
      flash('ok', `XML сформовано: ${r.mrid} (${num(r.totalEnergyMwh, 2, 'МВт·год')})`);
      await loadForDate();
    } catch (e: any) {
      flash(e.status === 409 ? 'warn' : 'err', e.message);
    } finally { setBusy(''); }
  };

  const downloadXml = () => {
    if (!mms?.xml) { flash('warn', 'Спершу сформуйте XML'); return; }
    const blob = new Blob([mms.xml], { type: 'application/xml' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = `${mms.mrid || 'mms'}.xml`; a.click();
    URL.revokeObjectURL(u);
  };

  const submitMms = async () => {
    if (!mms?.id) { flash('warn', 'Спершу сформуйте XML'); return; }
    setBusy('submit');
    try {
      const r = await api('/comdisp/mms/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: mms.id }),
      });
      flash('ok', r.note || 'Поставлено в чергу на відправку до MMS.');
      setBusy('');
      await loadForDate();
      // Host submitter runs on a ~2-min timer; refresh a few times to catch queued → submitted/error.
      for (let i = 0; i < 8; i++) { await new Promise((res) => setTimeout(res, 20000)); await loadForDate(); }
    } catch (e: any) { flash('err', e.message); } finally { setBusy(''); }
  };

  const gateInfo = useMemo(() => {
    const dl = mms?.gateDeadline || null;
    return { deadline: dl, past: dl ? new Date(dl).getTime() < Date.now() : false };
  }, [mms]);

  const shiftDate = (days: number) => setDate(new Date(new Date(date + 'T00:00:00').getTime() + days * 864e5).toLocaleDateString('en-CA'));
  const shiftMonth = (delta: number) => setMonth((m) => {
    const [y, mo] = m.split('-').map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const btn = (label: string, on: () => void, kind: 'primary' | 'ghost' | 'danger' = 'ghost', disabled = false) => (
    <button onClick={on} disabled={disabled}
      style={{
        padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, border: `1px solid ${kind === 'primary' ? accent : cardBorder}`,
        background: kind === 'primary' ? accent : kind === 'danger' ? 'rgba(255,107,107,0.12)' : 'transparent',
        color: kind === 'primary' ? '#001' : kind === 'danger' ? red : textMain,
      }}>{label}</button>
  );
  const arrow = (label: string, on: () => void) => (
    <button onClick={on} style={{ width: 26, height: 26, borderRadius: 6, cursor: 'pointer', border: `1px solid ${cardBorder}`, background: 'transparent', color: textMain, fontSize: 14, lineHeight: 1 }}>{label}</button>
  );

  const th: any = { textAlign: 'right', padding: '7px 12px', color: textMuted, fontSize: 11, fontWeight: 600, borderBottom: `1px solid ${cardBorder}` };
  const td: any = { textAlign: 'right', padding: '7px 12px', color: textMain, fontSize: 12.5, borderBottom: `1px solid ${rowBorder}` };

  const renderLegacy = () => (
    <DashboardLayout breadcrumb={['Дашборд', 'Диспетчеризація', 'Комерційна диспетчерська']}>
      <div style={{ color: textMain, maxWidth: '100%' }}>
        {/* header: asset + delivery date */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginRight: 8 }}>Комерційна диспетчерська</h1>
          <select value={assetId} onChange={(e) => { const a = assets.find((x) => x.id === e.target.value); if (a) pickAsset(a); }}
            style={{ background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: '7px 10px', fontSize: 13 }}>
            {assets.map((a) => <option key={a.id} value={a.id}>{short(a.assetName)}</option>)}
          </select>
          <label style={{ fontSize: 12, color: textMuted }}>Доба поставки:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {arrow('‹', () => shiftDate(-1))}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              style={{ background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, colorScheme: isDark ? 'dark' : 'light' }} />
            {arrow('›', () => shiftDate(1))}
          </div>
        </div>

        {/* ── Block 1: net forecast (sun + УЗЕ) ── */}
        <Card tk={tk} title="Підсумковий прогноз (сонячна генерація + УЗЕ)"
          right={<div style={{ fontSize: 12, color: textMuted }}>
            Сонячна {num(net ? net.pvKwh / 1000 : null, 1)} · Заряд −{num(net ? net.chargeKwh / 1000 : null, 1)} · Розряд +{num(net ? net.dischargeKwh / 1000 : null, 1)} · <b style={{ color: green }}>Разом {num(net ? net.netKwh / 1000 : null, 1)} МВт·год</b></div>}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} maxBarSize={28} barCategoryGap="16%">
                <CartesianGrid stroke={cardBorder} vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: textMuted, fontSize: 9 }} interval={0} />
                <YAxis width={46} tick={{ fill: textMuted, fontSize: 10 }} />
                <Tooltip contentStyle={{ background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 8, fontSize: 12, color: textMain }} formatter={(v: any, n: any) => [`${Number(v).toFixed(4)} МВт·год`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sun" name="Сонячна генерація" fill={accent} opacity={0.55} stackId="net" />
                <Bar dataKey="discharge" name="Розряд УЗЕ" fill={green} opacity={0.7} stackId="net" />
                <Bar dataKey="charge" name="Заряд УЗЕ" fill={amber} opacity={0.7} stackId="net" />
                <Line dataKey="net" name="Підсумок" stroke={green} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 8, border: `1px solid ${cardBorder}`, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${cardBorder}` }}>
              <div style={{ width: 46, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6, fontSize: 9, color: textMuted, borderRight: `1px solid ${cardBorder}` }}>Год</div>
              <div style={{ flex: 1, display: 'flex', paddingRight: 8 }}>
                {chartData.map((d: any, i: number) => <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: textMuted, padding: '3px 0', borderRight: i < chartData.length - 1 ? `1px solid ${cardBorder}` : 'none' }}>{d.hour}</div>)}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ width: 46, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6, fontSize: 9, color: textMuted, borderRight: `1px solid ${cardBorder}` }}>МВт·год</div>
              <div style={{ flex: 1, display: 'flex', paddingRight: 8 }}>
                {chartData.map((d: any, i: number) => <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 8, color: d.net < 0 ? amber : textMain, padding: '3px 0', borderRight: i < chartData.length - 1 ? `1px solid ${cardBorder}` : 'none' }}>{d.net.toFixed(2)}</div>)}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Block 2: MMS ── */}
        <Card tk={tk} title="Графік до MMS"
          subtitle={mms ? `${mms.mrid} · контрагент ${mms.counterparty} · ${num(mms.totalEnergyMwh, 2, 'МВт·год')}` : 'Ще не сформовано на цю добу'}
          right={<div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3].map((v) => (
              <button key={v} onClick={() => setRev(v)}
                style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: `1px solid ${rev === v ? accent : cardBorder}`, background: rev === v ? 'rgba(79,157,255,0.15)' : 'transparent', color: rev === v ? accent : textMuted }}>
                Верс. {v}</button>))}
          </div>}>
          <div style={{ background: gateInfo.past ? 'rgba(255,107,107,0.1)' : 'rgba(79,157,255,0.08)', border: `1px solid ${gateInfo.past ? red : accent}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, color: gateInfo.past ? red : textMain }}>
            {mms?.gateDeadline
              ? <>Версію {mms.revision || rev} потрібно завантажити на MMS <b>до {fmtKyiv(mms.gateDeadline)}</b>{gateInfo.past ? ' — дедлайн минув!' : ''}.</>
              : <>Дедлайни версій (D-1): верс.1 до 10:00 · верс.2 до 14:00 · верс.3 до 18:00. Графік формується лише на день D+1.</>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {btn(busy === 'gen' ? 'Формування…' : 'Сформувати XML', genXml, 'primary', busy === 'gen')}
            {btn('Завантажити XML', downloadXml, 'ghost', !mms?.xml)}
            {btn('Відправити на MMS', submitMms, 'ghost', !mms?.id || busy === 'submit')}
            {mms?.xml && btn(xmlOpen ? 'Сховати XML' : 'Показати XML', () => setXmlOpen(!xmlOpen))}
          </div>
          {mms?.status && mms.status !== 'generated' && (() => {
            const m: Record<string, { c: string; t: string }> = {
              queued: { c: amber, t: '⏳ У черзі на відправку до MMS…' },
              submitted: { c: '#37c871', t: '✅ Відправлено. Очікує підтвердження валідації (Acknowledgement за mRID).' },
              error: { c: red, t: `⚠ Помилка відправки: ${mms.errorMessage || ''}` },
              blocked: { c: amber, t: `⚠ ${mms.errorMessage || 'Відправку заблоковано'}` },
            };
            const s = m[mms.status] || { c: textMuted, t: `Статус: ${mms.status}` };
            return <div style={{ fontSize: 12, color: s.c, marginBottom: 8 }}>{s.t}</div>;
          })()}
          {xmlOpen && mms?.xml &&
            <pre style={{ background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: 12, fontSize: 11, color: codeColor, overflowX: 'auto', maxHeight: 320 }}>{mms.xml}</pre>}
        </Card>

        {/* ── УЗЕ editor ── */}
        <Card tk={tk} title="Графік заряду/розряду УЗЕ"
          subtitle="Заряд зменшує, розряд збільшує підсумковий прогноз"
          right={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={autofillBess} disabled={busy === 'autofill' || !bessCfg}
              title="Автозаповнення графіка УЗЕ (AI)"
              style={{ width: 34, height: 34, borderRadius: 8, fontSize: 12, fontWeight: 700, lineHeight: 1,
                cursor: (busy === 'autofill' || !bessCfg) ? 'not-allowed' : 'pointer', opacity: (busy === 'autofill' || !bessCfg) ? 0.5 : 1,
                border: `1px solid ${accent}`, background: 'transparent', color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {busy === 'autofill' ? '…' : 'AI'}
            </button>
            {btn(busy === 'bess' ? 'Збереження…' : 'Зберегти графік УЗЕ', saveBess, 'primary', busy === 'bess' || bessCheck.msgs.length > 0)}
          </div>}>
          {agentStatus && (agentStatus.status === 'emergency' || agentStatus.status === 'undercharge') && (
            <div style={{ background: agentStatus.status === 'emergency' ? 'rgba(255,107,107,0.12)' : 'rgba(245,166,35,0.12)', border: `1px solid ${agentStatus.status === 'emergency' ? red : amber}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: agentStatus.status === 'emergency' ? red : amber, marginBottom: 10 }}>
              🛡 Агент-запобіжник: {agentStatus.message}
            </div>
          )}
          {bessCheck.msgs.length > 0 && (
            <div style={{ background: 'rgba(255,107,107,0.1)', border: `1px solid ${red}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: red, marginBottom: 10 }}>
              ⚠ Технічно неможливі значення — виправте, щоб зберегти:
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{bessCheck.msgs.slice(0, 6).map((m: string, i: number) => <li key={i}>{m}</li>)}</ul>
              {bessCheck.msgs.length > 6 && <div style={{ marginTop: 2 }}>…та ще {bessCheck.msgs.length - 6}</div>}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 6 }}>
            {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => {
              const cur = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
              const err = bessCheck.errByHour[h];
              const col = err ? red : cur.mode === 'charge' ? amber : cur.mode === 'discharge' ? green : cardBorder;
              return (
                <div key={h} title={err || undefined} style={{ border: `1px solid ${col}`, borderRadius: 6, padding: 6, background: err ? 'rgba(255,107,107,0.08)' : innerBg }}>
                  <div style={{ fontSize: 10, color: textMuted, marginBottom: 3 }}>{String(h).padStart(2, '0')}</div>
                  <select value={cur.mode} onChange={(e) => setBessHour(h, { mode: e.target.value as Mode })}
                    style={{ width: '100%', background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 4, fontSize: 11, padding: '2px' }}>
                    <option value="idle">—</option>
                    <option value="charge">Заряд</option>
                    <option value="discharge">Розряд</option>
                  </select>
                  <input type="number" value={cur.powerKw || ''} placeholder="кВт" min={0} step={10}
                    onChange={(e) => setBessHour(h, { powerKw: Math.max(0, Number(e.target.value)) })}
                    style={{ width: '100%', marginTop: 3, background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 4, fontSize: 11, padding: '2px 4px' }} />
                </div>
              );
            })}
          </div>
        </Card>

        {/* ── Blocks 3 + 4/5 + 6 — three widgets, each 1/3 page width ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
        <Card tk={tk} title="Баланс (з початку місяця)"
          right={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {arrow('‹', () => shiftMonth(-1))}
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 120, textAlign: 'center' }}>{monthLabel(month)}</span>
            {arrow('›', () => shiftMonth(1))}
          </div>}>
          <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
              <div style={{ fontSize: 12, color: textMuted }}>Згенеровано</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: green }}>{num(balance ? balance.generatedMtdKwh / 1000 : null, 1, 'МВт·год')}</div>
            </div>
            <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
              <div style={{ fontSize: 12, color: textMuted }}>Передано в мережу</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{num(balance ? balance.deliveredMtdKwh / 1000 : null, 1, 'МВт·год')}</div>
            </div>
          </div>
          {balance?.deliveredSource === 'modbus_proxy' && <div style={{ fontSize: 11, color: amber, marginBottom: 8 }}>ⓘ «Передано в мережу» — попередньо за даними Modbus (комерційний лічильник ще не під'єднано)</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Дата</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Вироблено, МВт·год</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Передано, МВт·год</th>
              <th style={{ ...th, width: 36 }}></th>
            </tr></thead>
            <tbody>{(balance?.days || []).slice().reverse().map((d: any) => {
              const isOpen = openDay === d.day;
              return (
                <Fragment key={d.day}>
                  <tr onClick={() => toggleDay(d.day)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={td}>{num(d.generatedKwh / 1000, 1)}</td>
                    <td style={{ ...td, color: d.deliveredSource === 'meter' ? textMain : textMuted }}
                      title={d.deliveredSource === 'meter' ? 'Комерційний лічильник' : 'Попередньо за Modbus'}>
                      {num(d.deliveredKwh / 1000, 1)}{d.deliveredSource !== 'meter' ? ' *' : ''}</td>
                    <td style={{ ...td, textAlign: 'center', color: textMuted }}>{isOpen ? '⌄' : '›'}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0, background: innerBg }}>
                        <div style={{ padding: '6px 12px' }}>
                          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>Деталізація по годинах — {longDate(d.day)}</div>
                          {!dayHours[d.day] ? <div style={{ fontSize: 12, color: textMuted, padding: 6 }}>Завантаження…</div> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th style={{ ...th, textAlign: 'left' }}>Година</th><th style={th}>Вироблено, кВт·год</th><th style={th}>Передано, кВт·год</th></tr></thead>
                              <tbody>{dayHours[d.day].map((h: any) => (
                                <tr key={h.hour}><td style={{ ...td, textAlign: 'left' }}>{h.hour}</td><td style={td}>{num(h.generatedKwh, 0)}</td><td style={td}>{num(h.deliveredKwh, 0)}</td></tr>))}</tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}</tbody>
          </table>
        </Card>

          <Card tk={tk} title="Небаланси та вартість (БР)" subtitle="Прогноз − факт · × ціна балансуючого ринку · 30 днів">
            <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 1 calc(50% - 7px)', minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>Врегулювання</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: (imbPriced?.totalSettlementUah || 0) < 0 ? red : green }}>{num(imbPriced?.totalSettlementUah, 0, 'грн')}</div>
              </div>
            </div>
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Дата</th><th style={th}>Небаланс, кВт·год</th><th style={th}>Врегул., грн</th><th style={th}>год</th></tr></thead>
                <tbody>{(imbPriced?.days || []).slice().reverse().map((d: any) => (
                  <tr key={d.day}><td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={{ ...td, color: d.imbalanceKwh < 0 ? red : green }}>{num(d.imbalanceKwh, 0)}</td>
                    <td style={{ ...td, color: d.settlementUah < 0 ? red : green }}>{num(d.settlementUah, 0)}</td>
                    <td style={{ ...td, color: textMuted }}>{d.hoursPriced}/{d.hoursTotal}</td></tr>))}</tbody>
              </table>
            </div>
          </Card>

          <Card tk={tk} title="Недогенерація / втрачена вигода" subtitle="Прогноз − факт (з урахуванням ОСР) × РДН · 30 днів">
            <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>Втрачена вигода</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: red }}>{num(lost?.totalLostRevenueUah, 0, 'грн')}</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>ОСР</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: amber }}>{num(lost?.totalLostRevenueOsrUah, 0, 'грн')}</div>
              </div>
            </div>
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Дата</th><th style={th}>Недоген., кВт·год</th><th style={th}>Втрата, грн</th><th style={{ ...th, textAlign: 'center' }}>Причина</th></tr></thead>
                <tbody>{(lost?.days || []).slice().reverse().map((d: any) => (
                  <tr key={d.day}><td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={td}>{num(d.shortfallKwh, 0)}</td><td style={{ ...td, color: red }}>{num(d.lostRevenueUah, 0)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{d.anyConstraint ? <span style={{ fontSize: 10, color: amber, border: `1px solid ${amber}`, borderRadius: 4, padding: '1px 5px' }}>ОСР</span> : <span style={{ color: textMuted }}>—</span>}</td></tr>))}</tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50, maxWidth: 420, padding: '12px 16px', borderRadius: 10, fontSize: 13,
          background: cardBg, boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          border: `1px solid ${toast.kind === 'ok' ? green : toast.kind === 'warn' ? amber : red}`,
          color: toast.kind === 'ok' ? green : toast.kind === 'warn' ? amber : red }}>{toast.msg}</div>
      )}
    </DashboardLayout>
  );

  // ─────────────────────────── РЕДИЗАЙН (VITE_REDESIGN) ───────────────────────────
  // Той самий кабінет, що /forecast, /market/prices, /trading і Прогноз/MMS:
  // скляні .tile, токени, Urbanist, Lucide, один рівень поверхонь. Доба поставки
  // живе в топбарі (useComdispUi), станцію дає спільний селектор топбара.
  // Функціональність не змінена — ті самі стани, запити й обробники.

  /** Плитка-секція. Функція, а не компонент: не створює новий тип при рендері. */
  const rdCard = (o: { Icon: LucideIcon; title: string; subtitle?: any; right?: any; children: any }) => (
    <div className="tile p-4 sm:p-5">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <o.Icon size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
          <div className="min-w-0">
            <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{o.title}</h3>
            {o.subtitle && <div className="text-[11.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{o.subtitle}</div>}
          </div>
        </div>
        {o.right}
      </div>
      {o.children}
    </div>
  );

  /** Показник усередині плитки — без власної рамки (§3: один рівень). */
  const rdStat = (label: string, value: any, color = 'var(--text)') => (
    <div className="min-w-0">
      <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="font-display text-[17px] mt-0.5" style={{ color }}>{value}</div>
    </div>
  );

  /** Кнопка за стандартом хедер-контролів §6: 36 / radius 10. */
  const rdBtn = (label: string, on: () => void, o: { Icon?: LucideIcon; primary?: boolean; disabled?: boolean; title?: string } = {}) => (
    <button onClick={on} disabled={o.disabled} title={o.title}
      className="text-xs px-3 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors"
      style={{
        height: 36, borderRadius: 10,
        background: o.primary && !o.disabled ? 'var(--series-3)' : 'var(--surface-2)',
        color: o.disabled ? 'var(--text-dim)' : (o.primary ? '#fff' : 'var(--text)'),
        border: `1px solid ${o.primary && !o.disabled ? 'var(--series-3)' : 'var(--line)'}`,
        cursor: o.disabled ? 'not-allowed' : 'pointer', opacity: o.disabled ? 0.55 : 1,
      }}>
      {o.Icon && <o.Icon size={14} strokeWidth={2} />}
      {label}
    </button>
  );

  /** Смуга-повідомлення без вкладеної рамки: колір несе ліва акцентна межа. */
  const rdNote = (tone: 'ok' | 'warn' | 'err' | 'info', Icon: LucideIcon, body: any) => {
    const c = tone === 'ok' ? 'var(--ok)' : tone === 'err' ? 'var(--err)'
      : tone === 'warn' ? 'var(--warn)' : 'var(--text-muted)';
    return (
      <div className="text-[12px] flex items-start gap-2 py-2 pl-3 mb-3"
        style={{ borderLeft: `3px solid ${c}`, color: c }}>
        <Icon size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
        <div className="min-w-0">{body}</div>
      </div>
    );
  };

  const rdTh: any = { textAlign: 'right', padding: '7px 10px', color: 'var(--text-muted)', fontSize: 10.5,
                      fontWeight: 600, background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' };
  const rdTd: any = { textAlign: 'right', padding: '7px 10px', color: 'var(--text)', fontSize: 12.5,
                      borderBottom: '1px solid var(--line)' };

  const renderRd = () => (
    <DashboardLayout breadcrumb={['Передача до MMS']}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>
          {/* Заголовок у тілі сторінки не дублюємо: назву розділу вже показує
              топбар (breadcrumb вище). Верхній відступ тримає pt-5 на
              контейнері, а власний mb-5 пішов разом із блоком — плитки
              піднялись рівно на його місце, без порожнього простору. */}
          <div className="flex flex-col gap-3">
            {/* ── Підсумковий прогноз ─────────────────────────────────── */}
            {rdCard({
              Icon: Sun,
              title: 'Підсумковий прогноз',
              subtitle: 'сонячна генерація + УЗЕ',
              right: (
                <div className="flex items-baseline gap-3 text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                  <span>Сонячна <b style={{ color: 'var(--text)' }}>{num(net ? net.pvKwh / 1000 : null, 1)}</b></span>
                  <span>Заряд <b style={{ color: 'var(--warn)' }}>−{num(net ? net.chargeKwh / 1000 : null, 1)}</b></span>
                  <span>Розряд <b style={{ color: 'var(--ok-2)' }}>+{num(net ? net.dischargeKwh / 1000 : null, 1)}</b></span>
                  <span>Разом <b className="font-display" style={{ color: 'var(--text)' }}>{num(net ? net.netKwh / 1000 : null, 1)}</b> МВт·год</span>
                </div>
              ),
              children: (
                <>
                  <div style={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} maxBarSize={28} barCategoryGap="16%">
                        <CartesianGrid stroke={cc.grid} vertical={false} />
                        <XAxis dataKey="hour" tick={{ fill: cc.dim, fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                        <YAxis width={50} tick={{ fill: cc.dim, fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: cc.surface, border: `1px solid ${cc.grid}`, borderRadius: 10, fontSize: 12, color: cc.text }}
                          labelStyle={{ color: cc.text, fontWeight: 700 }}
                          formatter={(v: any, n: any) => [`${Number(v).toFixed(4)} МВт·год`, n]} />
                        <Bar dataKey="sun" name="Сонячна генерація" fill={cc.sun} fillOpacity={0.45} stackId="net" isAnimationActive={false} />
                        <Bar dataKey="discharge" name="Розряд УЗЕ" fill={cc.dis} fillOpacity={0.7} stackId="net" isAnimationActive={false} />
                        <Bar dataKey="charge" name="Заряд УЗЕ" fill={cc.chg} fillOpacity={0.7} stackId="net" isAnimationActive={false} />
                        <Line dataKey="net" name="Підсумок" stroke={cc.netLine} strokeWidth={2} dot={false} isAnimationActive={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-x-5 gap-y-1.5 flex-wrap pt-2 pb-3">
                    {[['Сонячна генерація', cc.sun], ['Розряд УЗЕ', cc.dis], ['Заряд УЗЕ', cc.chg], ['Підсумок', cc.netLine]].map(([l, c]) => (
                      <span key={l as string} className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        <span style={{ width: 16, height: 0, display: 'inline-block', borderTop: `3px solid ${c}` }} />{l}
                      </span>
                    ))}
                  </div>
                  {/* Погодинний підсумок: рядок значень під графіком, розділений
                      лініями замість вкладеної рамки (§3). */}
                  <div className="overflow-x-auto" style={{ borderTop: '1px solid var(--line)' }}>
                    <div className="flex" style={{ minWidth: 680 }}>
                      <div className="shrink-0 flex flex-col justify-around pr-2 text-[9px]"
                        style={{ width: 52, color: 'var(--text-muted)', borderRight: '1px solid var(--line)' }}>
                        <div className="text-right pt-1.5">Год</div>
                        <div className="text-right pb-1.5">МВт·год</div>
                      </div>
                      {chartData.map((d: any, i: number) => (
                        <div key={i} className="flex-1 text-center"
                          style={{ borderRight: i < chartData.length - 1 ? '1px solid var(--line)' : undefined }}>
                          <div className="text-[9px] pt-1.5" style={{ color: 'var(--text-muted)' }}>{d.hour}</div>
                          <div className="text-[9px] pb-1.5 font-display"
                            style={{ color: d.net < 0 ? 'var(--warn)' : 'var(--text)' }}>{d.net.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ),
            })}

            {/* ── Графік до MMS ───────────────────────────────────────── */}
            {rdCard({
              Icon: Send,
              title: 'Графік до MMS',
              subtitle: mms ? `${mms.mrid} · контрагент ${mms.counterparty} · ${num(mms.totalEnergyMwh, 2, 'МВт·год')}` : 'Ще не сформовано на цю добу',
              right: (
                <div className="flex items-center px-1 gap-0.5"
                  style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
                  {[1, 2, 3].map((v) => {
                    const on = rev === v;
                    return (
                      <button key={v} onClick={() => setRev(v)}
                        className="px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap flex items-center transition-colors"
                        style={{ height: 28, borderRadius: 8,
                                 background: on ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                                 border: `1px solid ${on ? 'var(--line-strong)' : 'transparent'}`,
                                 color: on ? 'var(--text)' : 'var(--text-muted)' }}>
                        Верс. {v}
                      </button>
                    );
                  })}
                </div>
              ),
              children: (
                <>
                  {mms?.gateDeadline
                    ? rdNote(gateInfo.past ? 'warn' : 'info', gateInfo.past ? AlertTriangle : Clock,
                        <>Версію {mms.revision || rev} потрібно завантажити на MMS <b>до {fmtKyiv(mms.gateDeadline)}</b>{gateInfo.past ? ' — дедлайн минув' : ''}.</>)
                    : rdNote('info', Clock,
                        <>Дедлайни версій (D-1): верс.1 до 10:00 · верс.2 до 14:00 · верс.3 до 18:00. Графік формується лише на добу D+1.</>)}

                  <div className="flex gap-2 flex-wrap">
                    {rdBtn(busy === 'gen' ? 'Формування…' : 'Сформувати XML', genXml, { Icon: FileCode2, primary: true, disabled: busy === 'gen' })}
                    {rdBtn('Завантажити XML', downloadXml, { Icon: Download, disabled: !mms?.xml })}
                    {rdBtn('Відправити на MMS', submitMms, { Icon: Send, disabled: !mms?.id || busy === 'submit' })}
                    {mms?.xml && rdBtn(xmlOpen ? 'Сховати XML' : 'Показати XML', () => setXmlOpen(!xmlOpen), { Icon: Code2 })}
                  </div>

                  {mms?.status && mms.status !== 'generated' && (() => {
                    // Догма: --err лише за реальним збоєм відправки. «У черзі» та
                    // «заблоковано» — попередження, не поломка.
                    const m: Record<string, { tone: 'ok' | 'warn' | 'err'; Icon: LucideIcon; t: string }> = {
                      queued: { tone: 'warn', Icon: Clock, t: 'У черзі на відправку до MMS…' },
                      submitted: { tone: 'ok', Icon: CheckCircle2, t: 'Відправлено. Очікує підтвердження валідації (Acknowledgement за mRID).' },
                      error: { tone: 'err', Icon: AlertCircle, t: `Помилка відправки: ${mms.errorMessage || ''}` },
                      blocked: { tone: 'warn', Icon: AlertTriangle, t: mms.errorMessage || 'Відправку заблоковано' },
                    };
                    const s = m[mms.status];
                    return <div className="mt-3">{s
                      ? rdNote(s.tone, s.Icon, s.t)
                      : rdNote('info', Info, `Статус: ${mms.status}`)}</div>;
                  })()}

                  {xmlOpen && mms?.xml && (
                    <pre className="mt-3 p-3 overflow-x-auto text-[11px]"
                      style={{ background: 'var(--surface-2)', borderRadius: 10, border: '1px solid var(--line)',
                               color: 'var(--text-muted)', maxHeight: 320 }}>{mms.xml}</pre>
                  )}
                </>
              ),
            })}

            {/* ── Графік заряду/розряду УЗЕ ───────────────────────────── */}
            {rdCard({
              Icon: BatteryCharging,
              title: 'Графік заряду/розряду УЗЕ',
              subtitle: 'заряд зменшує, розряд збільшує підсумковий прогноз',
              right: (
                <div className="flex gap-2 flex-wrap items-center">
                  <button onClick={autofillBess} disabled={busy === 'autofill' || !bessCfg}
                    title="Автозаповнення графіка УЗЕ (AI)"
                    className="inline-flex items-center justify-center gap-1.5 px-3 text-xs font-semibold"
                    style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)',
                             border: '1px solid var(--line)', color: 'var(--text)',
                             cursor: (busy === 'autofill' || !bessCfg) ? 'not-allowed' : 'pointer',
                             opacity: (busy === 'autofill' || !bessCfg) ? 0.55 : 1 }}>
                    <Sparkles size={14} strokeWidth={2} />{busy === 'autofill' ? '…' : 'AI'}
                  </button>
                  {rdBtn(busy === 'bess' ? 'Збереження…' : 'Зберегти графік УЗЕ', saveBess,
                    { Icon: Save, primary: true, disabled: busy === 'bess' || bessCheck.msgs.length > 0 })}
                </div>
              ),
              children: (
                <>
                  {agentStatus && (agentStatus.status === 'emergency' || agentStatus.status === 'undercharge') &&
                    rdNote(agentStatus.status === 'emergency' ? 'err' : 'warn', ShieldAlert,
                      <>Агент-запобіжник: {agentStatus.message}</>)}

                  {bessCheck.msgs.length > 0 && rdNote('err', AlertCircle, (
                    <>
                      Технічно неможливі значення — виправте, щоб зберегти:
                      <ul className="mt-1 ml-4 list-disc">{bessCheck.msgs.slice(0, 6).map((m: string, i: number) => <li key={i}>{m}</li>)}</ul>
                      {bessCheck.msgs.length > 6 && <div className="mt-0.5">…та ще {bessCheck.msgs.length - 6}</div>}
                    </>
                  ))}

                  {/* 24 години — рівно 12 колонок на десктопі, тобто два ряди:
                      01–12 і 13–24. Було auto-fit, через що кількість колонок
                      «плавала» від ширини і ряди виходили нерівними. Кожна
                      колонка 1fr → однакова ширина, gap-2 → рівні відступи по
                      обох осях. На вузьких екранах сітка згортається (4 / 6 / 8),
                      усі варіанти діляться на 24 без «хвоста». Рендер годин
                      (map по масиву) не змінено. */}
                  <div className="grid gap-2 grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12">
                    {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => {
                      const cur = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
                      const err = bessCheck.errByHour[h];
                      // Смуга-акцент замість рамки навколо комірки (§3).
                      const bar = err ? 'var(--err)'
                        : cur.mode === 'charge' ? 'var(--warn)'
                        : cur.mode === 'discharge' ? 'var(--ok-2)' : 'var(--line)';
                      return (
                        <div key={h} title={err || undefined} className="pl-2 py-1"
                          style={{ borderLeft: `3px solid ${bar}` }}>
                          <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>{String(h).padStart(2, '0')}</div>
                          <select value={cur.mode} onChange={(e) => setBessHour(h, { mode: e.target.value as Mode })}
                            className="w-full text-[11px] outline-none px-1"
                            style={{ height: 28, borderRadius: 8, background: 'var(--surface-2)',
                                     border: '1px solid var(--line)', color: 'var(--text)' }}>
                            <option value="idle">—</option>
                            <option value="charge">Заряд</option>
                            <option value="discharge">Розряд</option>
                          </select>
                          <input type="number" value={cur.powerKw || ''} placeholder="кВт" min={0} step={10}
                            onChange={(e) => setBessHour(h, { powerKw: Math.max(0, Number(e.target.value)) })}
                            className="w-full text-[11px] outline-none px-1.5 mt-1"
                            style={{ height: 28, borderRadius: 8, background: 'var(--surface-2)',
                                     border: '1px solid var(--line)', color: 'var(--text)' }} />
                        </div>
                      );
                    })}
                  </div>
                </>
              ),
            })}

            {/* ── Баланс · Небаланси · Недогенерація ──────────────────── */}
            <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              {rdCard({
                Icon: Scale,
                title: 'Баланс',
                subtitle: 'з початку місяця',
                right: (
                  <div className="flex items-center gap-1">
                    <button onClick={() => shiftMonth(-1)} className="flex items-center justify-center cursor-pointer shrink-0"
                      style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text)' }}>
                      <ChevronLeft size={15} strokeWidth={2} />
                    </button>
                    <span className="text-[12px] font-semibold text-center" style={{ minWidth: 118, color: 'var(--text)' }}>{monthLabel(month)}</span>
                    <button onClick={() => shiftMonth(1)} className="flex items-center justify-center cursor-pointer shrink-0"
                      style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text)' }}>
                      <ChevronRight size={15} strokeWidth={2} />
                    </button>
                  </div>
                ),
                children: (
                  <>
                    <div className="grid grid-cols-2 gap-4 pb-3 mb-1" style={{ borderBottom: '1px solid var(--line)' }}>
                      {rdStat('Згенеровано', num(balance ? balance.generatedMtdKwh / 1000 : null, 1, 'МВт·год'))}
                      {rdStat('Передано в мережу', num(balance ? balance.deliveredMtdKwh / 1000 : null, 1, 'МВт·год'))}
                    </div>
                    {balance?.deliveredSource === 'modbus_proxy' && rdNote('info', Info,
                      <>«Передано в мережу» — попередньо за даними Modbus (комерційний лічильник ще не під&apos;єднано)</>)}
                    <div className="overflow-x-auto">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <th style={{ ...rdTh, textAlign: 'left' }}>Дата</th>
                          <th style={{ ...rdTh, whiteSpace: 'nowrap' }}>Вироблено, МВт·год</th>
                          <th style={{ ...rdTh, whiteSpace: 'nowrap' }}>Передано, МВт·год</th>
                          <th style={{ ...rdTh, width: 32 }}></th>
                        </tr></thead>
                        <tbody>{(balance?.days || []).slice().reverse().map((d: any) => {
                          const isOpen = openDay === d.day;
                          return (
                            <Fragment key={d.day}>
                              <tr onClick={() => toggleDay(d.day)} style={{ cursor: 'pointer' }}>
                                <td style={{ ...rdTd, textAlign: 'left' }}>{shortDate(d.day)}</td>
                                <td style={rdTd}>{num(d.generatedKwh / 1000, 1)}</td>
                                <td style={{ ...rdTd, color: d.deliveredSource === 'meter' ? 'var(--text)' : 'var(--text-muted)' }}
                                  title={d.deliveredSource === 'meter' ? 'Комерційний лічильник' : 'Попередньо за Modbus'}>
                                  {num(d.deliveredKwh / 1000, 1)}{d.deliveredSource !== 'meter' ? ' *' : ''}</td>
                                <td style={{ ...rdTd, textAlign: 'center', color: 'var(--text-muted)' }}>
                                  {isOpen ? <ChevronDown size={13} strokeWidth={2} /> : <ChevronRight size={13} strokeWidth={2} />}
                                </td>
                              </tr>
                              {isOpen && (
                                <tr>
                                  <td colSpan={4} style={{ padding: 0 }}>
                                    <div className="px-2 py-2">
                                      <div className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
                                        Деталізація по годинах — {longDate(d.day)}
                                      </div>
                                      {!dayHours[d.day]
                                        ? <div className="text-[12px] p-1.5" style={{ color: 'var(--text-muted)' }}>Завантаження…</div>
                                        : (
                                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead><tr>
                                              <th style={{ ...rdTh, textAlign: 'left' }}>Година</th>
                                              <th style={rdTh}>Вироблено, кВт·год</th>
                                              <th style={rdTh}>Передано, кВт·год</th>
                                            </tr></thead>
                                            <tbody>{dayHours[d.day].map((h: any) => (
                                              <tr key={h.hour}>
                                                <td style={{ ...rdTd, textAlign: 'left' }}>{h.hour}</td>
                                                <td style={rdTd}>{num(h.generatedKwh, 0)}</td>
                                                <td style={rdTd}>{num(h.deliveredKwh, 0)}</td>
                                              </tr>))}</tbody>
                                          </table>
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}</tbody>
                      </table>
                    </div>
                  </>
                ),
              })}

              {rdCard({
                Icon: Coins,
                title: 'Небаланси та вартість (БР)',
                subtitle: 'прогноз − факт · × ціна балансуючого ринку · 30 днів',
                children: (
                  <>
                    <div className="pb-3 mb-1" style={{ borderBottom: '1px solid var(--line)' }}>
                      {/* Гроші нейтральні; знак показуємо догмою: ↑ --ok / ↓ --warn. */}
                      {rdStat('Врегулювання', num(imbPriced?.totalSettlementUah, 0, 'грн'),
                        (imbPriced?.totalSettlementUah || 0) < 0 ? 'var(--warn)' : 'var(--ok)')}
                    </div>
                    <div className="overflow-x-auto" style={{ maxHeight: 440, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <th style={{ ...rdTh, textAlign: 'left' }}>Дата</th>
                          <th style={rdTh}>Небаланс, кВт·год</th>
                          <th style={rdTh}>Врегул., грн</th>
                          <th style={rdTh}>год</th>
                        </tr></thead>
                        <tbody>{(imbPriced?.days || []).slice().reverse().map((d: any) => (
                          <tr key={d.day}>
                            <td style={{ ...rdTd, textAlign: 'left' }}>{shortDate(d.day)}</td>
                            <td style={{ ...rdTd, color: d.imbalanceKwh < 0 ? 'var(--warn)' : 'var(--ok)' }}>{num(d.imbalanceKwh, 0)}</td>
                            <td style={{ ...rdTd, color: d.settlementUah < 0 ? 'var(--warn)' : 'var(--ok)' }}>{num(d.settlementUah, 0)}</td>
                            <td style={{ ...rdTd, color: 'var(--text-muted)' }}>{d.hoursPriced}/{d.hoursTotal}</td>
                          </tr>))}</tbody>
                      </table>
                    </div>
                  </>
                ),
              })}

              {rdCard({
                Icon: TrendingDown,
                title: 'Недогенерація / втрачена вигода',
                subtitle: 'прогноз − факт (з урахуванням ОСР) × РДН · 30 днів',
                children: (
                  <>
                    <div className="grid grid-cols-2 gap-4 pb-3 mb-1" style={{ borderBottom: '1px solid var(--line)' }}>
                      {/* Недобір — бурштин, не червоний: це не збій системи (догма §3). */}
                      {rdStat('Втрачена вигода', num(lost?.totalLostRevenueUah, 0, 'грн'), 'var(--warn)')}
                      {rdStat('ОСР', num(lost?.totalLostRevenueOsrUah, 0, 'грн'), 'var(--warn)')}
                    </div>
                    <div className="overflow-x-auto" style={{ maxHeight: 440, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <th style={{ ...rdTh, textAlign: 'left' }}>Дата</th>
                          <th style={rdTh}>Недоген., кВт·год</th>
                          <th style={rdTh}>Втрата, грн</th>
                          <th style={{ ...rdTh, textAlign: 'center' }}>Причина</th>
                        </tr></thead>
                        <tbody>{(lost?.days || []).slice().reverse().map((d: any) => (
                          <tr key={d.day}>
                            <td style={{ ...rdTd, textAlign: 'left' }}>{shortDate(d.day)}</td>
                            <td style={rdTd}>{num(d.shortfallKwh, 0)}</td>
                            <td style={{ ...rdTd, color: 'var(--warn)' }}>{num(d.lostRevenueUah, 0)}</td>
                            <td style={{ ...rdTd, textAlign: 'center' }}>
                              {d.anyConstraint
                                ? <span className="text-[10px] px-1.5 py-0.5" style={{ color: 'var(--warn)', border: '1px solid var(--warn)', borderRadius: 6 }}>ОСР</span>
                                : <span style={{ color: 'var(--text-dim)' }}>—</span>}
                            </td>
                          </tr>))}</tbody>
                      </table>
                    </div>
                  </>
                ),
              })}
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="tile fixed z-50 px-4 py-3 text-[13px] flex items-start gap-2"
          style={{ bottom: 24, right: 24, maxWidth: 420,
                   color: toast.kind === 'ok' ? 'var(--ok)' : toast.kind === 'warn' ? 'var(--warn)' : 'var(--err)' }}>
          {toast.kind === 'ok' ? <CheckCircle2 size={15} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
            : toast.kind === 'warn' ? <AlertTriangle size={15} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
            : <AlertCircle size={15} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />}
          <span>{toast.msg}</span>
        </div>
      )}
    </DashboardLayout>
  );

  return isRd ? renderRd() : renderLegacy();
}
